import * as Sentry from '@sentry/react-native'

/** Report an error that was caught and handled (e.g. shown as a generic
 *  error message) but should not happen in normal operation — lets it
 *  show up in Sentry even though the user already got a graceful fallback. */
export function reportUnexpectedError(error: unknown, context?: Record<string, unknown>): void {
  Sentry.captureException(error, context ? { extra: context } : undefined)
}

/** Drop-in for useQuery's `retry` option — reproduces react-query's default
 *  retry count (3) but reports exactly once, on the final failed attempt,
 *  instead of once per attempt (which would report the same incident up to
 *  4 times: the initial try plus 3 retries). */
export function reportOnFinalRetry(failureCount: number, error: unknown): boolean {
  if (failureCount < 3) return true
  reportUnexpectedError(error)
  return false
}

/** Same idea as reportOnFinalRetry, for queries that also poll via
 *  refetchInterval — retry keeps working normally (transient blips still
 *  self-heal), but reporting is capped to once per mount via the given ref,
 *  since refetchInterval re-triggers a whole new attempt sequence on its
 *  own schedule regardless of the retry setting, and would otherwise report
 *  again every poll for as long as a real outage lasts. */
export function createReportOnceRetry(hasReportedRef: { current: boolean }) {
  return (failureCount: number, error: unknown): boolean => {
    if (failureCount < 3) return true
    if (!hasReportedRef.current) {
      hasReportedRef.current = true
      reportUnexpectedError(error)
    }
    return false
  }
}
